import { load } from 'scrivito'

import { SavedFilterStructure } from '@/Atomic/Organisms/FilterBuilder'
import { Filter } from '@/Data/Filter/FilterDataClass'

export interface SavedFilter {
  id: string
  title: string
  entity: string
  filter: SavedFilterStructure
  favorite: boolean
  kind: 'neocrm' | 'classic'
}

export const generateUniqueName = (
  baseName: string,
  existingNames: string[],
): string => {
  if (!existingNames.includes(baseName)) return baseName
  let counter = 1
  let candidateName = `${baseName} (${counter})`
  while (existingNames.includes(candidateName)) {
    counter++
    candidateName = `${baseName} (${counter})`
  }
  return candidateName
}

const toSavedFilter = (item: {
  id: () => string
  get: (field: string) => unknown
}): SavedFilter => ({
  id: item.id(),
  title: item.get('title') as string,
  entity: item.get('entity') as string,
  favorite: (item.get('favorite') as boolean) ?? false,
  kind: (item.get('kind') as 'neocrm' | 'classic') ?? 'classic',
  filter: (item.get('query') as { filters?: SavedFilterStructure })
    ?.filters ?? {
    or: [],
  },
})

/**
 * Reactive saved-filters hook. The list is a live read of the Filter DataClass —
 * a synchronous `.take()` inside a `connect()`-wrapped consumer, the same pattern
 * as useDataClassIndex / useDetailsTabData. Because the read is reactive, ANY
 * mutation of a Filter record re-renders the consumer with fresh data, including
 * creates made outside this hook (e.g. the AiChatBot save_filter tool). No
 * optimistic local mirror is needed — the URL `filter` param is the selection,
 * and this list is the store.
 */
export const useSavedFilters = (entityName: string) => {
  let allFilters: SavedFilter[] = []
  try {
    const items = Filter.all()
      .transform({ filters: { entity: entityName } })
      .take()
    allFilters = items.filter((item) => !!item.get('title')).map(toSavedFilter)
  } catch {
    // take() throws while the scope is still loading; render empty until ready.
    // Scrivito re-renders this connect()-wrapped consumer once the scope
    // resolves, so no loading flag is needed — the empty render self-heals.
  }

  const createFilter = async (
    title: string,
    filter: SavedFilterStructure,
  ): Promise<SavedFilter> => {
    const item = await load(() =>
      Filter.create({
        entity: entityName,
        title,
        favorite: false,
        kind: 'neocrm',
        query: { filters: filter },
      }),
    )
    return toSavedFilter(item)
  }

  const updateFilter = async (
    id: string,
    updates: Partial<Pick<SavedFilter, 'title' | 'filter'>>,
  ): Promise<void> => {
    await load(() => {
      const item = Filter.get(id)
      if (!item) return

      const updateData: Record<string, unknown> = {}
      if (updates.title !== undefined) {
        updateData.title = updates.title
      }
      if (updates.filter !== undefined) {
        updateData.query = { filters: updates.filter }
      }

      return item.update(updateData)
    })
  }

  const deleteFilter = async (id: string): Promise<void> => {
    await load(() => {
      const item = Filter.get(id)
      return item?.delete()
    })
  }

  // Reads current favorite state via load() rather than closing over the render's
  // list, so the write is never based on stale data.
  const toggleFavorite = async (id: string): Promise<void> => {
    try {
      await load(() => {
        const item = Filter.get(id)
        if (!item) return
        return item.update({ favorite: !item.get('favorite') })
      })
    } catch (error) {
      console.error('Failed to update favorite:', error)
    }
  }

  const duplicateFilter = async (id: string): Promise<SavedFilter | null> => {
    const original = await load(() => {
      const item = Filter.get(id)
      return item ? toSavedFilter(item) : null
    })
    if (!original) return null
    const existingNames = await load(() =>
      Filter.all()
        .transform({ filters: { entity: original.entity } })
        .take()
        .map((item) => item.get('title') as string),
    )
    const newName = generateUniqueName(original.title, existingNames)
    return createFilter(newName, JSON.parse(JSON.stringify(original.filter)))
  }

  // Pure derivations of the reactive list — recomputed each render, no memo needed.
  const isFavorite = (id: string): boolean =>
    allFilters.find((f) => f.id === id)?.favorite ?? false

  const getFilter = (id: string): SavedFilter | undefined =>
    allFilters.find((f) => f.id === id)

  // Backwards compat: neocrm-only filters
  const savedFilters = allFilters.filter((f) => f.kind === 'neocrm')

  return {
    allFilters,
    savedFilters,
    createFilter,
    updateFilter,
    deleteFilter,
    duplicateFilter,
    getFilter,
    toggleFavorite,
    isFavorite,
  }
}
